import { Component, input } from '@angular/core';
import { JunoError } from '../juno-error/juno-error';
import { FormValueClassAbstract } from '../../../abstract/form-value-class-abstract';
import { TInputType } from '@dss-libs/shared/type/common-types';
import { MatIcon } from '@angular/material/icon';
import { TranslocoDirective } from '@jsverse/transloco';

/**
 * A styled, accessible text / number / email / search input built on top of
 * {@link FormValueClassAbstract} and integrated with Angular Signal Forms.
 *
 * The component wraps a native `<input>` element, forwards all Signal Forms
 * state (validation errors, touched, disabled …) automatically through the
 * abstract base class, and renders inline validation messages via
 * `<juno-error>`.
 *
 * ---
 *
 * @template T  The value type – `string` for text/email/search inputs, `number`
 *              for `type="number"`. Inherited from
 *              `InputValueClassAbstract<string | number, string>`.
 *
 * ---
 *
 * ### Inputs (own)
 *
 * | Input             | Type          | Default  | Description                                      |
 * |-------------------|---------------|----------|--------------------------------------------------|
 * | `type`            | `TInputType`  | `'text'` | Native `<input>` type (`text`, `number`, `email`, `search`, …). |
 * | `prefixIconConfig`| `{ icon: string; iconClasses: string }` | `{ icon: '', iconClasses: '' }` | When `icon` is non-empty, a `<mat-icon>` is rendered inside the leading slot of the input. |
 * | `maxLength`       | `number \| undefined` | `undefined` | Inherited from {@link FormControlStateAbstract}; the native input stops accepting characters past it. |
 *
 * ### Inputs (inherited from {@link FormValueClassAbstract})
 *
 * `value`, `label`, `labelClass`, `placeholder`, `name`, `appliedClasses`,
 * `required`, `pattern`, `errors`, `pending`, `touched`, `dirty`, `disabled`, `maxLength`.
 *
 * ### Outputs (inherited)
 *
 * `touch` – emitted on blur so Signal Forms marks the field as touched.
 *
 * ---
 *
 * @usageNotes
 *
 * #### With Signal Forms (`[formField]`)
 * ```html
 * <juno-libs-ui-input
 *   [formField]="userForm.name"
 *   label="GENERAL.NAME"
 *   placeholder="Enter your name"
 * />
 *
 * <juno-libs-ui-input
 *   [formField]="userForm.age"
 *   label="GENERAL.AGE"
 *   type="number"
 * />
 *
 * <juno-libs-ui-input
 *   [formField]="userForm.email"
 *   label="GENERAL.EMAIL"
 *   type="email"
 *   placeholder="Enter a valid email"
 * />
 * ```
 *
 * #### Standalone two-way binding (`[(value)]`)
 * ```html
 * <juno-libs-ui-input
 *   [(value)]="searchSignal"
 *   type="search"
 *   placeholder="Search…"
 *   label="GENERAL.SEARCH"
 *   name="search"
 *   [prefixIconConfig]="{ icon: 'minimalistic-magnifier-outline', iconClasses: 'text-ink-muted!' }"
 * />
 * ```
 *
 * @see {@link FormValueClassAbstract} for the full list of inherited inputs/outputs.
 * @see {@link JunoSelect} for the dropdown counterpart.
 */
@Component({
  selector: 'juno-libs-ui-input',
  imports: [JunoError, MatIcon, TranslocoDirective],
  templateUrl: './juno-input.html',
  styleUrl: './juno-input.css',
})
export class JunoInput extends FormValueClassAbstract<string | number, string> {
  type = input<Omit<TInputType, 'radio' | 'checkbox' | 'date'>>('text');
  prefixIconConfig = input<{ icon: string; iconClasses: string }>({ icon: '', iconClasses: '' });
}
